import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AggType, BaseConfigItem, ChartConfigItem, FieldMetadata, LabelValuePair, TooltipConfigItem, VisualizationType } from '@ddv/models';
import { uid } from '@ddv/utils';

import { ConfigurationZoneChange } from '../../../models/configuration-zone-change';
import { ChartDropZoneConfig } from '../../../models/drop-zone-config';
import { DropZoneId } from '../../../models/drop-zone-id';
import { getAggregationDropdownList } from '../../../utils/get-aggregation-dropdown-list';
import { isValueZone } from '../../../utils/is-value-zone';

@Component({
    selector: 'app-ddv-configuration-zone',
    templateUrl: './configuration-zone.component.html',
    styleUrls: ['./configuration-zone.component.scss'],
    standalone: false,
})
export class ConfigurationZoneComponent {
    @Input() zone: ChartDropZoneConfig | undefined;
    @Input() dataConfigs: BaseConfigItem[] | ChartConfigItem[] = [];
    @Input() isReadOnly = false;
    @Input() activelyDragging = false;
    @Input() visualizationType: VisualizationType | undefined;
    @Output() configurationZoneChange = new EventEmitter<ConfigurationZoneChange>();
    @Output() configurationFieldDrop = new EventEmitter<TooltipConfigItem>();

    protected onDrop(data: CdkDragDrop<BaseConfigItem[]>, zone: ChartDropZoneConfig): void {
        if (data.previousContainer === data.container) {
            moveItemInArray(data.container.data, data.previousIndex, data.currentIndex);
        } else {
            if (zone.allowed) {
                const metadata = data.item.data as FieldMetadata;
                const defaultAggItem = this.getDefaultAggItem(metadata, zone.id);
                this.updateConfigList(this.dataConfigs, metadata, zone, data.currentIndex, defaultAggItem);
                if (zone.id !== 'tooltip') {
                    const defaultTooltipAggItem = this.getDefaultAggItem(metadata, 'tooltip');
                    const configItem = this.getTooltipConfigItem(metadata, defaultTooltipAggItem);
                    this.configurationFieldDrop.emit(configItem);
                }
            }
        }
        this.activelyDragging = false;
        this.onConfigurationZoneChange();
    }

    protected removeConfig(colId: string): void {
        const index = this.dataConfigs.findIndex((config) => config.colId === colId);
        if (index === -1) {
            return;
        }

        this.dataConfigs.splice(index, 1);
        this.onConfigurationZoneChange();
    }

    private getDefaultAggItem(metadata: FieldMetadata, zoneId: DropZoneId): LabelValuePair<AggType> | undefined {
        const aggItems = getAggregationDropdownList(
            metadata.displayType ?? 'string',
            zoneId,
            this.visualizationType ?? 'HORIZONTAL_BAR_CHART',
        );
        return aggItems.length ? aggItems[0] : undefined;
    }

    private updateConfigList(
        configList: BaseConfigItem[],
        metadata: FieldMetadata,
        zone: ChartDropZoneConfig,
        index: number,
        defaultAggItem: LabelValuePair<AggType> | undefined,
    ): void {
        const configItem = this.getConfigItem(zone.id, metadata, defaultAggItem);
        this.dataConfigs = [...configList.slice(0, index), configItem, ...configList.slice(index)];
    }

    private getConfigItem(
        zone: DropZoneId,
        metadata: FieldMetadata,
        defaultAggItem: LabelValuePair<AggType> | undefined,
    ): BaseConfigItem | ChartConfigItem | TooltipConfigItem {
        if (zone === 'tooltip') {
            return this.getTooltipConfigItem(metadata, defaultAggItem);
        }

        if (isValueZone(zone)) {
            return this.getChartConfigItem(metadata, defaultAggItem);
        }

        return this.getBaseConfigItem(metadata);
    }

    private getTooltipConfigItem(metadata: FieldMetadata, defaultAggItem: LabelValuePair<AggType> | undefined): TooltipConfigItem {
        return {
            ...this.getBaseConfigItem(metadata),
            aggregationType: defaultAggItem?.value ?? 'first',
            numberFormat: metadata.displayType === 'value' ? 'XX.00' : undefined,
            decimalPlaces: metadata.displayType === 'value' ? 2 : undefined,
        };
    }

    private getChartConfigItem(metadata: FieldMetadata, defaultAggItem: LabelValuePair<AggType> | undefined): ChartConfigItem {
        return {
            ...this.getBaseConfigItem(metadata),
            aggregationType: defaultAggItem?.value ?? 'first',
        };
    }

    private getBaseConfigItem(metadata: FieldMetadata): BaseConfigItem {
        return {
            colId: uid(),
            label: metadata.displayName ?? '',
            value: metadata.name,
            datatype: metadata.datatype ?? '',
            displayType: metadata.displayType ?? 'string',
            showCustomName: false,
            customName: null,
        };
    }

    private onConfigurationZoneChange(): void {
        if (!this.zone) {
            return;
        }
        this.configurationZoneChange.emit({ dataConfigs: this.dataConfigs, key: this.zone.configKey });
    }
}
